import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { QRScanner, QRScannerStatus } from '@ionic-native/qr-scanner/ngx/';
import { Vibration } from '@awesome-cordova-plugins/vibration/ngx/';
import { Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, first, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ITenantLite } from 'src/app/services/tenant/models/tenant-lite.model';
import { ITenant } from 'src/app/services/tenant/models/tenant.model';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { IUser } from 'src/app/services/user/models/user.model';
import { UserService } from 'src/app/services/user/user.service';
import { EventService } from 'src/app/services/events/event.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { ErrorService } from '@app/services/error/error.service';

@Component({
  selector: 'app-go',
  templateUrl: './go.page.html',
  styleUrls: ['./go.page.scss'],
})
export class GoPage implements OnInit {

  private unSubscribe = new Subject<void>();
  private reloadPageEventSubscription: Subscription = null;

  public  user = this.userService.userSignal;
  public tenant = this.tenantService.tenantSignal;

  public overlay: boolean = false;
  public idCodeMarkup: boolean = false;
  public userManageIdCode: boolean = false;
  public userManagePinCode: boolean = false;
  public userManageCard: boolean = false;
  public showRegisterCardDeniedDialog: boolean = false;
  public gettingIdCode: boolean = true;
  public gettingPinCode: boolean = true;
  public idCode: string = null;
  public idCodeResource: any = null;
  public pinCodeResource: any = null;
  public isIOS: boolean = this.platformService.isIOS();
  public isCordova: boolean = this.platformService.isCordova();


  public nav: any;
  public newPinCode: string = '';
  public enterPinCode: boolean = false;
  public pinCodeConflict: boolean = false;
  public invalidPinCode: boolean = false;
  public incompletePinCode: boolean = true;

  public cardCode: string = '';
  public enterCardCode: boolean = false;
  public invalidCardCode: boolean = false;
  public invalidQRCode: boolean = false;
  public incompleteCardCode: boolean = true;
  public cards: Array<any> = [];

  public getTenantAndUserSubscription = new Subject<void>()
  public showTransparentBody: boolean = false;
  public isOnline = this.networkService.hasInternetAccess;

  private logger: Logger;

  constructor(
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private eventService: EventService,
    private loggingService: LoggingService,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    private platform: Platform,
    private platformService: PlatformService,
    private registerCardScannerQR: QRScanner,
    private ref: ChangeDetectorRef,
    private storageService: StorageService,
    private tenantService: TenantService,
    private toastController: ToastController,
    private translateService: TranslateService,
    private userService: UserService,
    private vibration: Vibration,
  ) {
    this.logger = loggingService.getLogger("[GoPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('CardsAndCodes');
  }

  ionViewWillLeave(): void {
    this.invalidQRCode = false;
    this.toggleQRScanningState(false);
    this.reloadPageEventSubscription.unsubscribe();
  }

  ionViewDidEnter(): void {
    this.pageTitleService.setTitle('CardsAndCodes');
    this.dialogService.showLoadingSpinnerDialog('ionViewDidEnter->refeshCurrentTenant()').subscribe(() => {
      this.tenantService.refreshStoredTenant()
      .pipe(takeUntil(this.getTenantAndUserSubscription))
      .subscribe(() => {
        this.userService.refreshCurrentUser()
        .pipe(takeUntil(this.getTenantAndUserSubscription))
        .subscribe(() => {
          this.dialogService.hideLoadingSpinnerDialog('ionViewDidEnter->refeshCurrentTenant()');
          this.getTenantAndUserSubscription.next();
          this.getTenantAndUserSubscription.complete();
          this.eventService.startTokenExpireCountDown();
          this.setListeners();
          this.initPage();
        });
      }, (error) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - ionViewDidEnter->refeshCurrentTenant()');
        this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      });
    });
  }

  private setListeners() {
    this.reloadPageEventSubscription = this.broadcaster.on('reload_page').subscribe(() => {
      this.logger.info('Token expire countdown - reload_page');
       location.reload();
    });
  }

  private initPage () {
    this.logger.info('initPage()');
    this.pinCodeConflict = false;
    this.handleUserPermissions();
    this.getPinCode().subscribe();
    this.getIdCode().subscribe();
    this.getCards().subscribe();
    this.checkParams();
  }

  private handleUserPermissions() {
    this.logger.info('handleUserPermissions()');
    let userPermissions = this.user().embedded.permissions.permissions;
    const userPermissionsSet = new Set(userPermissions);

    if (userPermissionsSet.has('RESOURCE_PINCODE_CREATE_OWN')) {
      this.userManagePinCode = true;
    }
    if (userPermissionsSet.has('RESOURCE_ID_CODES_CREATE_OWN')) {
      this.userManageIdCode = true;
    }
    if (userPermissionsSet.has('RESOURCE_CARD_CREATE_OWN')) {
      this.userManageCard = true;
    }
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// CHECK PARAMS
  ///////////////////////////////////////////
  private checkParams () {
    this.logger.info('checkParams()');
    let params = location.search;
    if (params.includes('registrationCode') || params.includes('registrationCode=FORGOT_CODE')) {
      if (!this.userManageCard) { // does the user have permission to register a card?
        this.logger.info('checkParams() - showRegisterCardDeniedToast');
        let permissionMessage = this.translateMessageWithParameter('ACCESS_DENIED');
        this.errorToast(permissionMessage);
        return;
      } else if (params.includes('registrationCode') && !params.includes('registrationCode=FORGOT_CODE')) {
        let splitParams = params.split('&');
        for (let i = 0; i < splitParams.length; i++) {
          if (splitParams[i].includes('registrationCode')) {
            this.cardCode = splitParams[i].split('=')[1];
            if (this.cardCode) {
              this.registerCardWebAppScan();
            }
          }
        }
      } else {
        if (params.includes('registrationCode=FORGOT_CODE')) {
          this.enterCardCode = true;
        }
      }
    }
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// CARD STUFF
  ///////////////////////////////////////////
  public getCards (): Observable<any> {
    this.logger.info('getCards()');
    return this.user().api.getCards(this.user()).pipe(
      catchError((error: any) => {
        this.logger.error('getCards() error:', JSON.stringify(error));
        this.cards = [];
        return of(null); // Return null on error to continue execution
      }),
      tap((cards: any) => {
        if (cards && cards._embedded) {
          this.cards = cards._embedded['px:cards'];
        } else {
          this.cards = [];
        }
      })
    );
  }

  public deleteCard (card: any) {
    this.logger.info('deleteCard()');
    this.dialogService.showLoadingSpinnerDialog('deleteCard()').subscribe(() => {
      this.user().api.deleteCard(card).subscribe(() => {
        this.dialogService.hideLoadingSpinnerDialog('deleteCard()');
        let message = this.translateMessageWithParameter('CardDeleted');
        this.successToast(message);
        this.getCards().subscribe();
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('deleteCard()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
      });
    });
  }

  public registerCardDialog () {
    this.logger.info('registerCardDialog()');
    this.enterPinCode = false;
    this.cardCode = '';
    this.invalidCardCode = false;
    this.enterCardCode = !this.enterCardCode;
  }

  public checkCardCode (event) {
    this.logger.info('checkCardCode()');

    this.cardCode = event;
    let numRegex = /^[0-9]\d*$/;
    if (!numRegex.test(this.cardCode)) {
      this.invalidCardCode = true;
    } else {
      this.invalidCardCode = false;
    }

    if (this.cardCode.length < 6) {
      this.incompleteCardCode = true;
    }

    if (this.cardCode.length === 0) {
      this.invalidCardCode = false;
    }

    if (numRegex.test(this.cardCode) && this.cardCode.length === 6) {
      this.invalidCardCode = false;
      this.incompleteCardCode = false;
    }
  }

  public registerCardWebAppScan () {
    this.logger.info('registerCardWebAppScan()');
    this.dialogService.showLoadingSpinnerDialog('registerCardWebAppScan()').subscribe(() => {
      this.enterCardCode = false;
      this.user().api.registerCard(this.user(), this.cardCode).subscribe((response) => {
        this.dialogService.hideLoadingSpinnerDialog('registerCardWebAppScan()');
        let message = this.translateMessageWithParameter('CardRegistered');
        this.successToast(message);
        this.getCards().subscribe();
        history.pushState({}, null, '/');
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - registerCardWebAppScan()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
        history.pushState({}, null, '/');
      });
    });
  }

  public registerCard () {
    this.logger.info('registerCard()');
    this.dialogService.showLoadingSpinnerDialog('registerCard()').subscribe(() => {
      this.enterCardCode = false;
      this.user().api.registerCard(this.user(), this.cardCode).subscribe((response) => {
        let message = this.translateMessageWithParameter('CardRegistered');
        this.successToast(message);
        setTimeout(() => {
          this.dialogService.hideLoadingSpinnerDialog('registerCard()');
          this.getCards().subscribe();
        }, 1000);
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('registerCard()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        } else if (httpErrorResponse.status === 404) {
          message = this.translateMessageWithParameter('InvalidCardCode');
        }
        this.errorToast(message);
      });
    });
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// ID CODE STUFF
  ///////////////////////////////////////////
  public getIdCode(): Observable<any> {
    this.logger.info('getIdCode()');
    this.gettingIdCode = true;
    return this.user().api.getIdCode(this.user()).pipe(
      catchError((error) => {
        this.logger.error('getIdCode() error:', JSON.stringify(error));
        this.gettingIdCode = false;
        return of(null); // Return null on error to continue execution
      }),
      tap((idCode) => {
        this.gettingIdCode = false;
        if (idCode) {
          this.idCodeResource = idCode;
        }
      })
    );
  }

  public setIdCode() {
    this.logger.info('setIdCode()');
    this.dialogService.showLoadingSpinnerDialog('setIdCode()').subscribe(() => {
      this.user().api.setIdCode(this.tenant()).subscribe((idCode) => {
         this.dialogService.hideLoadingSpinnerDialog('setIdCode()');
        this.idCodeResource = idCode;
        let message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - setIdCode()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
      });
    });
  }

  public resetIdCode() {
    this.logger.info('resetIdCode()');
    this.user().api.deleteIdCode(this.idCodeResource).subscribe((response) => {
      this.setIdCode();
    }, (httpErrorResponse) => {
      let message = this.translateMessageWithParameter('ERROR');
      if (httpErrorResponse.status === 403) {
        message = this.translateMessageWithParameter('ACCESS_DENIED');
      }
      this.errorToast(message);
    });
  }

  public deleteIdCode() {
    this.logger.info('deleteIdCode()');
    this.dialogService.showLoadingSpinnerDialog('deleteIdCode()').subscribe(() => {
      this.user().api.deleteIdCode(this.idCodeResource).subscribe((response) => {
        this.dialogService.hideLoadingSpinnerDialog('deleteIdCode()');
        this.idCodeResource = null;
        let message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - deleteIdCode()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
      });
    });
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// PIN CODE STUFF
  ///////////////////////////////////////////
  public getPinCode (): Observable<any> {
    this.logger.info('getPinCode()');
    this.gettingPinCode = true;
    return this.user().api.getPinCode(this.user()).pipe(
      catchError((error) => {
        this.logger.error('getPinCode() error:', JSON.stringify(error));
        this.gettingPinCode = false;
        return of(null); // Return null on error to continue execution
      }),
      tap((response) => {
        this.gettingPinCode = false;
        if (response) {
          this.pinCodeResource = response;
        }
      })
    );
  }

  public toggleEnterPinCode () {
    this.logger.info('toggleEnterPinCode()');

    this.enterPinCode = !this.enterPinCode;
    this.newPinCode = '';
    this.invalidPinCode = false;
    this.enterCardCode = false;
  }

  public checkPinCode (event) {
    this.logger.info('checkPinCode()');

    this.newPinCode = event;
    let numRegex = /^[0-9]\d*$/;
    if (!numRegex.test(this.newPinCode)) {
      this.invalidPinCode = true;
    } else {
      this.invalidPinCode = false;
    }

    if (this.newPinCode.length < 4) {
      this.incompletePinCode = true;
    }

    if (this.newPinCode.length === 0) {
      this.invalidPinCode = false;
    }

    if (numRegex.test(this.newPinCode) && this.newPinCode.length === 4) {
      this.invalidPinCode = false;
      this.incompletePinCode = false;
    }
  }

  public setPinCode () {
    this.logger.info('setPinCode()');
    this.enterPinCode = false;
    this.pinCodeConflict = false;
    this.dialogService.showLoadingSpinnerDialog('setPinCode()').subscribe(() => {
      this.user().api.setPinCode(this.user(), this.newPinCode).subscribe((response) => {
        this.dialogService.hideLoadingSpinnerDialog('setPinCode()');
        this.pinCodeResource = response;
        let message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - setPinCode()');
        if (httpErrorResponse.status === 409) {
          this.pinCodeConflict = true;
        } else {
          let message = this.translateMessageWithParameter('ERROR');
          if (httpErrorResponse.status === 403) {
            message = this.translateMessageWithParameter('ACCESS_DENIED');
          }
          this.errorToast(message);
        }

      });
    });
  }

  public deletePinCode() {
    this.logger.info('deletePinCode()');
    this.dialogService.showLoadingSpinnerDialog('deletePinCode()').subscribe(() => {
      this.user().api.deletePinCode(this.pinCodeResource).subscribe((response) => {
        this.dialogService.hideLoadingSpinnerDialog('deletePinCode()');
        this.pinCodeResource = null;
        let message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - deletePinCode()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
      });
    });
  }

  public showPinCodeTitle(): boolean {
    return this.pinCodeResource ||
           !this.idCodeResource;
  }

  public showPinCodeRequiredText(): boolean {
    return this.idCodeResource &&
           this.idCodeResource.pincodeRequired &&
           !this.pinCodeResource;
  }

  public showResetPinCodeButton(): boolean {
    return !this.gettingPinCode &&
           this.pinCodeResource &&
           !this.enterPinCode &&
           this.userManagePinCode;
  }

  public showCreatePinCodeButton(): boolean {
    return !this.gettingPinCode &&
           !this.pinCodeResource &&
           !this.enterPinCode &&
           this.userManagePinCode;
  }

  public showPinCodeDisabled(): boolean {
    return this.pinCodeResource &&
           this.pinCodeResource.disabled &&
           !this.pinCodeConflict;
  }

  public showPinCodeDots(): boolean {
    return !this.gettingPinCode &&
           this.pinCodeResource &&
           !this.enterPinCode &&
           !this.pinCodeResource.disabled &&
           !this.pinCodeConflict;
  }

  public showDeletePinCodeIcon(): boolean {
    return !this.gettingPinCode &&
           this.pinCodeResource &&
           !this.enterPinCode &&
           !this.pinCodeConflict &&
           this.userManagePinCode;
  }

  public showNoPinCodeDash(): boolean {
    return !this.gettingPinCode &&
           !this.pinCodeResource &&
           !this.enterPinCode;
  }

  public showResetIdCodeButton(): boolean {
    return this.idCodeResource &&
           this.userManageIdCode;
  }

  public showCreateIdCodeButton(): boolean {
    return !this.idCodeResource &&
           this.userManageIdCode;
  }

  public showNoIdCodeDash(): boolean {
    return !this.gettingIdCode &&
           !this.idCodeResource;
  }

  public showDeleteIdCodeIcon(): boolean {
    return this.idCodeResource &&
           this.userManageIdCode;
  }

  public showRegisterCardDialogButton(): boolean {
    return !this.enterCardCode &&
           this.userManageCard;
  }

  public showRegisterCardForm(): boolean {
    return this.enterCardCode &&
           this.userManageCard;
  }

  public showDeleteCardIcon(): boolean {
    return this.userManageCard;
  }



  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// TOAST
  ///////////////////////////////////////////
  private translateMessageWithParameter(text: string, args?: string[]): string {
    if (text) {
      this.translateService.get(text)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((value: string) => {
        text = value;
        this.unSubscribe.next();
        this.unSubscribe.complete();
      });
      return text;
    }
  }

  private successToast (message: string) {
    let toast = this.toastController.create({
      message: message,
      color: 'success',
      duration: 3000,
      position: 'top'
    }).then(toast => toast.present());
  }

  private errorToast (message: string) {
    let toast = this.toastController.create({
      message: message,
      color: 'danger',
      duration: 3000,
      position: 'top'
    }).then(toast => toast.present());
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// QR SCANNER STUFF
  ///////////////////////////////////////////

  // button to open camera and scan qr code
  public openQRScanner(): void { // VIEW
    const methodName = 'QR openQRScanner() ';
    this.logger.info(methodName);

    this.registerCardScannerQR.prepare().then((status: QRScannerStatus) => {
      if (status.authorized) {
        this.toggleQRScanningState(true);
        this.scanQRCode();
      } else if (status.denied) {
        // camera permission was permanently denied
        // you must use QRScanner.openSettings() method to guide the user to the settings page
        // then they can grant the permission from there
        this.logger.info(methodName + 'status.denied, the user needs to authorize the app to use the camera from the settings, in order to scan QR codes');
        this.dialogService.showAlertDialog('You need to authorize the app to use the camera from the settings, in order to scan QR codes').subscribe();
      } else {
        this.logger.info(methodName + 'status.denied, the user needs to authorize the app to use the camera from the settings, in order to scan QR codes');
        this.dialogService.showAlertDialog('You need to authorize the app to use the camera from the settings, in order to scan QR codes').subscribe();
        // permission was denied, but not permanently. You can ask for permission again at a later time.
      }
    })
    .catch((e: any) => {
      this.logger.info(methodName + 'qrScanner.prepare() ERROR: ', e);
    });
  };

  public toggleQRScanningState(on: boolean): void {
    this.logger.info('toggleQRScanningState()');

    if (on) {
      window.document.querySelector('ion-app').classList.add('transparentBody');
      window.document.querySelector('ion-content').classList.add('transparentBody');
      this.registerCardScannerQR.show();
    } else {
      window.document.querySelector('ion-app').classList.remove('transparentBody');
      window.document.querySelector('ion-content').classList.remove('transparentBody');
      this.registerCardScannerQR.hide();
      this.registerCardScannerQR.destroy();
    }
    this.showTransparentBody = on;
    this.ref.detectChanges();
  }

  public scanQRCode(): void { // openQRScanner()
    this.logger.info('scanQRCode()');

    this.registerCardScannerQR.scan()
    .pipe(takeUntil(this.unSubscribe))
    .subscribe((qrUrl: string) => {
      this.logger.info('scanQRCode() qrScanner.scan()');
      this.vibration.vibrate(1000);
      this.unSubscribe.next();
      this.unSubscribe.complete();
      this.cardCode = qrUrl.split('=')[1];
      this.cancelQRScanner();
      this.registerCard();
    });
  }

  public cancelQRScanner(): void {
    this.logger.info('cancelQRScanner()');
    setTimeout(() => {
      this.invalidQRCode = false;
      this.toggleQRScanningState(false);
    }, 300);
  };
}
